import './quietConsole.js';
/**
 * bewonersbrief-bridge.js
 *
 * Hook tussen de Sevenn GIS-frontend en de bewonersbrief.be Next.js wizard
 * (de parent van deze iframe). Stuurt een postMessage zodra een drawing
 * server-side gemodificeerd is (save / delete / edit), zodat de wizard
 * z'n Bevestig-knop kan activeren zonder polling.
 *
 * Strategie: we wrappen `window.fetch`. Bij een succesvolle response op
 * een van de draw-mutating endpoints sturen we `gis-selection-update`
 * naar de parent met de actuele totale count en alle polygons.
 *
 * De payload die we naar `/draw/save` POSTen ís de autoritatieve nieuwe
 * state - die gebruiken we direct als bron, in plaats van met een
 * timeout een verse GET `/draw/load` te doen. Dat oude pad had een
 * read-after-write race: de backend was binnen 150ms vaak nog niet
 * commit-zichtbaar, waardoor de parent de vorige state binnenkreeg
 * (rechterkolom-counter liep 1 tekening achter).
 *
 * Geen wijzigingen aan upstream Sevenn-files nodig - bij een
 * `git merge upstream/production` zit deze file ongewijzigd op zijn plek.
 */

const MUTATING_PATHS = [
  '/spatial-search/draw/save',
  '/spatial-search/draw/delete',
  // Voeg later toe als upstream nieuwe endpoints introduceert die de
  // drawing-set wijzigen (edit, batch-delete, etc.).
];

const originalFetch = window.fetch.bind(window);

window.fetch = async function patchedFetch(...args) {
  const requestBody = extractRequestBody(args);
  const response = await originalFetch.apply(this, args);
  try {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url ?? '';
    if (response.ok && MUTATING_PATHS.some((p) => url.includes(p))) {
      notifyParentFromPayload(requestBody);
    }
  } catch {
    // Interceptor mag nooit de oorspronkelijke fetch laten falen.
  }
  return response;
};

function extractRequestBody(args) {
  const init = args[1];
  if (init && typeof init.body === 'string') return init.body;
  return null;
}

function notifyParentFromPayload(body) {
  if (window.parent === window) return; // niet in iframe → niets te notifyen
  if (!body) return;
  try {
    const parsed = JSON.parse(body);
    if (!Array.isArray(parsed)) return;

    // Hervorm naar de shape die de parent al kent van /draw/load:
    //   { seq, color, count, geometry, address_data }
    // De /save payload gebruikt camelCase `addressData` - we mappen dat.
    const drawings = parsed.map((item) => ({
      seq: item.seq,
      color: item.color,
      count: item.count,
      geometry: item.geometry,
      address_data: item.addressData ?? item.address_data ?? '[]',
    }));

    const totalCount = drawings.reduce((sum, d) => sum + (d?.count ?? 0), 0);

    window.parent.postMessage(
      {
        type: 'gis-selection-update',
        count: totalCount,
        drawings,
      },
      '*',
    );
  } catch (err) {
    // Logger noise; bridge moet geen fouten gooien
    // eslint-disable-next-line no-console
    console.warn('[bewonersbrief-bridge] notifyParent failed:', err);
  }
}

// Stuur ook een "ready"-event zodat de parent weet dat de iframe geladen is.
window.addEventListener('DOMContentLoaded', () => {
  if (window.parent === window) return;
  window.parent.postMessage({ type: 'gis-frontend-ready' }, '*');
});
